import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, Link } from "react-router-dom";
import Breadcrumbs from "../components/common/Breadcrumbs";
import { fetchUser } from "../store/slices/authSlice";
import { fetchDashboardStats } from "../store/slices/dashboardSlice";
import MainLayout from "../components/layout/MainLayout";
import Chart from "react-apexcharts";

const Dashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Redux Selectors
  const { user, isAuthenticated, isLoading: authLoading } = useSelector((state) => state.auth);
  const {
    stats,
    genderStats,
    programStats,
    monthlyTrends,
    activities,
    isLoading: dashLoading
  } = useSelector((state) => state.dashboard);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/login");
      return;
    }
    if (!user) {
      dispatch(fetchUser());
    }
    dispatch(fetchDashboardStats());
  }, [isAuthenticated, user, dispatch, navigate]);

  if (authLoading || dashLoading || !user || !stats) {
    return (
      <MainLayout>
        <div className="dashboard-container text-center p-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </MainLayout>
    );
  }

  // ────────────────────────────────────────────────
  //  CHART CONFIGURATIONS
  // ────────────────────────────────────────────────

  const monthlyTrendsOptions = {
    chart: { type: "bar", height: 320, toolbar: { show: false }, fontFamily: "Inter, sans-serif" },
    plotOptions: { bar: { horizontal: false, columnWidth: "50%", borderRadius: 6 } },
    dataLabels: { enabled: false },
    stroke: { show: true, width: 2, colors: ["transparent"] },
    xaxis: { categories: monthlyTrends?.labels || [] },
    yaxis: { title: { text: "Count" } },
    fill: { opacity: 1 },
    colors: ["#7367f0", "#28c76f"],
    legend: { position: "top" },
  };

  const monthlyTrendsSeries = [
    { name: "Admissions", data: monthlyTrends?.admissions || [] },
    { name: "Inquiries", data: monthlyTrends?.inquiries || [] },
  ];

  const genderDonutOptions = {
    chart: { type: "donut", height: 300, fontFamily: "Inter, sans-serif" },
    labels: genderStats?.labels || ["No Data"],
    colors: ["#7367f0", "#ff6f91", "#ffc75f", "#00cfe8"],
    legend: { position: "bottom" },
    plotOptions: { pie: { donut: { size: "65%", labels: { show: true, total: { show: true, label: "Total", formatter: () => stats.totalPatients || 0 } } } } },
    dataLabels: { enabled: true },
  };
  const genderDonutSeries = genderStats?.series || [];

  const programBarOptions = {
    chart: { type: "bar", height: 300, toolbar: { show: false }, fontFamily: "Inter, sans-serif" },
    plotOptions: { bar: { horizontal: true, barHeight: "55%", borderRadius: 6, distributed: true } },
    dataLabels: { enabled: true },
    xaxis: { categories: programStats?.labels || [] },
    colors: ["#7367f0", "#28c76f", "#ff9f43", "#00cfe8", "#ea5455"],
    legend: { show: false },
  };
  const programBarSeries = [{ name: "Cases", data: programStats?.series || [] }];

  const admissionsTrendOptions = {
    chart: { type: "line", height: 80, sparkline: { enabled: true } },
    stroke: { width: 2.5, curve: "smooth" },
    colors: ["#7367f0"],
  };
  const admissionsTrendSeries = [{ data: monthlyTrends?.admissions || [] }];

  return (
    <MainLayout>
      <div className="content-wrapper">
        <Breadcrumbs />
        <div className="container-xxl flex-grow-1 container-p-y">
          <div className="row g-6">

            {/* ─── Welcome Card ─── */}
            <div className="col-md-12 col-xxl-8">
              <div className="card">
                <div className="d-flex align-items-end row">
                  <div className="col-md-6 order-2 order-md-1">
                    <div className="card-body">
                      <h4 className="card-title mb-4">
                        Welcome back, <span className="fw-bold">{user.name}</span> 🎉
                      </h4>
                      <p className="mb-0">
                        You joined us on{" "}
                        <strong>{new Date(user.created_at).toLocaleDateString()}</strong>
                      </p>
                      <p></p>
                      <Link to="/patients" className="btn btn-primary">
                        View Patients
                      </Link>
                    </div>
                  </div>
                  <div className="col-md-6 text-center text-md-end order-1 order-md-2">
                    <div className="card-body pb-0 px-0 pt-2">
                      <img
                        src="/assets/img/illustrations/illustration-john-light.png"
                        height="186"
                        className="scaleX-n1-rtl"
                        alt="Welcome"
                      />
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* ─── Stat Cards ─── */}
            <div className="col-xxl-2 col-sm-6">
              <div className="card h-100 shadow-sm">
                <div className="card-body">
                  <div className="avatar mb-4">
                    <div className="avatar-initial bg-label-primary rounded-3"><i className="ri-user-heart-line ri-24px"></i></div>
                  </div>
                  <h4 className="mb-1 fw-bold">{stats.totalPatients ?? 'N/A'}</h4>
                  <p className="mb-0">Total Active Clients</p>
                </div>
              </div>
            </div>

            <div className="col-xxl-2 col-sm-6">
              <div className="card h-100 shadow-sm">
                <div className="card-header pb-0">
                  <h4 className="mb-1 fw-bold">{stats.activeCases ?? 'N/A'}</h4>
                  <span className="d-block card-subtitle text-muted">Active Registrations</span>
                </div>
                <div className="card-body">
                  <Chart options={admissionsTrendOptions} series={admissionsTrendSeries} type="line" height={80} />
                </div>
              </div>
            </div>

            {/* ─── Bar Chart Row ─── */}
            <div className="col-12 col-xxl-8">
              <div className="card h-100 shadow-sm">
                <div className="row row-bordered g-0 h-100">
                  <div className="col-md-7 col-12 border-end">
                    <div className="card-header"><h5 className="mb-0">Monthly Admissions vs Inquiries</h5></div>
                    <div className="card-body"><Chart options={monthlyTrendsOptions} series={monthlyTrendsSeries} type="bar" height={280} /></div>
                  </div>
                  <div className="col-md-5 col-12">
                    <div className="card-header"><h5 className="mb-1">Summary</h5></div>
                    <div className="card-body pt-6">
                      <div className="row g-4 text-center">
                        <div className="col-6 border-end">
                          <p className="mb-1 small">Total Discharges</p>
                          <h6 className="mb-0">{stats.totalDischarges ?? 'N/A'}</h6>
                        </div>
                        <div className="col-6">
                          <p className="mb-1 small">Total Inquiries</p>
                          <h6 className="mb-0">{stats.totalInquiries ?? 'N/A'}</h6>
                        </div>
                      </div>
                      <hr className="my-5" />
                      <div className="text-center">
                        <p className="mb-1 text-muted">Active Rehab Plans</p>
                        <h4 className="mb-4">{stats.rehabPlans ?? 'N/A'}</h4>
                        <Link to="/cases" className="btn btn-primary w-100">View All Registrations</Link>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-12 col-xxl-4 col-md-6">
              <div className="card h-100 shadow-sm">
                <div className="card-header"><h5 className="mb-0">Gender Distribution</h5></div>
                <div className="card-body"><Chart options={genderDonutOptions} series={genderDonutSeries} type="donut" height={300} /></div>
              </div>
            </div>

            <div className="col-md-6 col-xxl-4">
              <div className="card h-100 shadow-sm">
                <div className="card-header"><h5 className="mb-0">Registrations by Program</h5></div>
                <div className="card-body"><Chart options={programBarOptions} series={programBarSeries} type="bar" height={300} /></div>
              </div>
            </div>

            {/* ─── Updated Recent Activity Timeline ─── */}
            <div className="col-md-12 col-xxl-8">
              <div className="card h-100 shadow-sm">
                <div className="card-header d-flex justify-content-between align-items-center">
                  <h5 className="mb-0">Recent Activity</h5>
                  <small className="text-muted">Last 5 actions</small>
                </div>
                <div className="card-body pt-4">
                  <ul className="timeline card-timeline mb-0">
                    {activities && activities.length > 0 ? (
                      activities.map((act, index) => {
                        // Extracting actioned_by and actioned_on from description if possible
                        // Or using the activity structure from your backend
                        const userName = act.description?.split(':')[0]?.replace('User ', '') || 'Rayyan';
                        const mainMsg = act.description?.split(':')[1]?.trim() || act.description;

                        return (
                          <li key={index} className="timeline-item timeline-item-transparent">
                            <span className={`timeline-point timeline-point-${act.color || 'primary'}`}></span>
                            <div className="timeline-event">
                              <div className="row w-100 align-items-center">
                                {/* Left Side: Two Lines (Actioned by / Actioned on) */}
                                <div className="col-md-3 text-start">
                                  <div className="d-flex flex-column">
                                    <small className="text-muted mb-1" style={{ fontSize: '0.75rem' }}>
                                      Actioned by: <span className="text-dark fw-semibold">{userName}</span>
                                    </small>
                                    <small className="text-muted" style={{ fontSize: '0.75rem' }}>
                                      Actioned on: <span className="text-dark">{act.timeAgo}</span>
                                    </small>
                                  </div>
                                </div>

                                {/* Middle: Main Message */}
                                <div className="col-md-7 text-center">
                                  <p className="mb-0 fw-medium text-secondary">{mainMsg || 'N/A'}</p>
                                </div>

                                {/* Right Side: Label based on color */}
                                <div className="col-md-2 text-end">
                                  <span className={`badge bg-label-${act.color || 'primary'} rounded-pill px-3`}>
                                    {act.title || 'Actioned'}
                                  </span>
                                </div>
                              </div>
                              <hr className="my-3 opacity-50" />
                            </div>
                          </li>
                        );
                      })
                    ) : (
                      <p className="text-center text-muted">No recent activity found.</p>
                    )}
                  </ul>
                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default Dashboard;